import { Amount } from '@ui/components/Amount/Amount';
import { Change } from '@ui/components/Amount/Change';
import { TextTooltip } from '@ui/components/TextTooltip';
import { useGetMaxTakeProfitP } from '@ui/features/Trade/hooks/useGetMaxTakeProfitP';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { Text } from '@ui/lib/park/ui/text';
import { useGetPairMaxTpRatios } from '@ui/services/contract/hooks/TradingConfigFacet/useGetPairMaxTpRatios/useGetPairMaxTpRatios';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { getLeverage } from '@ui/utils/trade/getLeverage';
import { getMaxTpRatios } from '@ui/utils/trade/getMaxTpRatios';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { Box, VStack } from 'styled-system/jsx';

import { valueStyle } from '../AddCollateral/styles';
import { useLeverageChangeLatestPrice } from './hooks/useLeverageChange';
import { useLiquidationPriceChange } from './hooks/useLiquidationPriceChange';
import { useMaxRemovableCollateral } from './hooks/useMaxRemovableCollateral';
import { useWithdrawCollateralInput } from './hooks/useWithdrawCollateralInput';

export const CollateralChange = ({ position }: { position: Position }) => {
  const price = usePayTokenPriceLatest(position.collateralToken);
  const { input } = useWithdrawCollateralInput();

  if (!price) return <Box />;
  const collateralUsd = position.getCollateralUsd(price);
  const nextCollateralUsd = collateralUsd.minus(input.value.usd ?? 0);

  return (
    <Change
      containerDataCy="RemoveCollateralChange"
      nextValue={nextCollateralUsd}
      prevValue={collateralUsd}
      opts={{
        prefix: '$',
      }}
    />
  );
};

export const CollateralRatioChange = ({ position }: { position: Position }) => {
  const price = usePayTokenPriceLatest(position.collateralToken);
  const { input } = useWithdrawCollateralInput();
  const pairPrice = usePriceLatest(position.pair.baseSymbol);

  if (!price || !pairPrice) return <Box />;
  const collateralUsd = position.getCollateralUsd(price);
  const nextCollateralUsd = collateralUsd.minus(input.value.usd ?? 0);
  const sizeUsd = position.getSizeUsd(pairPrice);

  return (
    <Change
      containerDataCy="RemoveCollateralRatioChange"
      nextValue={nextCollateralUsd.div(sizeUsd)}
      prevValue={collateralUsd.div(sizeUsd)}
      opts={{
        isShowSign: false,
        suffix: '%',
      }}
    />
  );
};

export const LeverageChange = ({ position }: { position: Position }) => {
  const { t } = useTranslation();
  const change = useLeverageChangeLatestPrice({
    position,
  });
  const maxRemovableResult = useMaxRemovableCollateral({ position });

  if (!change?.prev) return <Box />;

  const leverageAtMaxRemoval = maxRemovableResult?.leverageAfterMaxRemoval;
  const showCapNote =
    (maxRemovableResult?.limitReason === 'liquidation' ||
      maxRemovableResult?.limitReason === 'takeProfit') &&
    leverageAtMaxRemoval;

  return (
    <VStack gap={0.5} alignItems="flex-end" data-cy="RemoveLeverageCell">
      <Change
        containerDataCy="RemoveLeverageChange"
        nextValue={change.next}
        prevValue={change.prev}
        opts={{
          suffix: 'x',
          displayDecimals: 1,
        }}
      />
      {showCapNote && (
        <TextTooltip
          content={
            <VStack gap={1} alignItems="flex-start">
              <Text textStyle="text.small" color="white">
                {t('common.Not all collateral can be withdrawn.')}
              </Text>
              <Text textStyle="text.small" color="white">
                {maxRemovableResult?.limitReason === 'takeProfit'
                  ? t('common.Removing more would exceed max TP.')
                  : t('common.Removing more would risk liquidation.')}
              </Text>
            </VStack>
          }
          contentCss={{ maxW: '240px', textAlign: 'left' }}
        >
          <Text
            textStyle="text.xsmall"
            color="greyWhite.lightGrey3"
            textAlign="right"
            textDecoration="underline dotted"
            cursor="help"
            data-cy="RemoveLeverageCapNote"
          >
            {t('common.Max ~{leverage}x', {
              leverage: leverageAtMaxRemoval.toFixed(1),
            })}
          </Text>
        </TextTooltip>
      )}
    </VStack>
  );
};

export const TakeProfitChange = ({ position }: { position: Position }) => {
  const { t } = useTranslation();
  const priceDisplayDecimals = useTokenDisplayDecimals(
    position.pair.baseSymbol,
    position.pair.priceDisplayDecimals,
  );
  const nextTakeProfit = useNextTakeProfitAfterRemoval(position);

  if (position.takeProfit.isZero()) {
    return (
      <Text css={valueStyle}>
        {t('common.Not Set')}
      </Text>
    );
  }

  if (!nextTakeProfit || nextTakeProfit.eq(position.takeProfit)) {
    return (
      <Amount
        css={valueStyle}
        value={position.takeProfit}
        opts={{ prefix: '$', displayDecimals: priceDisplayDecimals }}
      />
    );
  }

  return (
    <Change
      containerDataCy="RemoveTakeProfitChange"
      nextValue={nextTakeProfit}
      prevValue={position.takeProfit}
      opts={{ prefix: '$', displayDecimals: priceDisplayDecimals }}
    />
  );
};

const useNextTakeProfitAfterRemoval = (position: Position) => {
  const collateralPrice = usePayTokenPriceLatest(position.collateralToken);
  const { input } = useWithdrawCollateralInput();
  const maxTakeProfitP = useGetMaxTakeProfitP();
  const { data: currentPairMaxTpRatios } = useGetPairMaxTpRatios({
    pairBase: position.pair.address,
  });

  return useMemo(() => {
    if (
      !collateralPrice ||
      !input.value.token ||
      input.value.token.lte(0) ||
      !currentPairMaxTpRatios ||
      maxTakeProfitP === undefined ||
      position.takeProfit.isZero()
    ) {
      return undefined;
    }

    const collateralAfterRemoval = position.collateral.minus(input.value.token);
    const collateralAfterRemovalUsd =
      collateralAfterRemoval.times(collateralPrice);
    if (collateralAfterRemovalUsd.lte(0)) return undefined;

    const entryNotionalUsd = position.size.times(position.entryPrice);
    const leverage = getLeverage({
      sizeUsd: entryNotionalUsd,
      collateralUsd: collateralAfterRemovalUsd,
    });
    if (leverage.lte(0)) return undefined;

    const nextMaxTakeProfitP = getMaxTpRatios({
      leverage: leverage.toNumber(),
      currentPairMaxTpRatios,
      maxTakeProfitP,
    });
    const priceOffset = position.entryPrice
      .times(nextMaxTakeProfitP)
      .div(leverage);
    const maxTakeProfitPrice = position.isLong
      ? position.entryPrice.plus(priceOffset)
      : BigNumber.max(position.entryPrice.minus(priceOffset), 0);

    if (position.isLong) {
      return position.takeProfit.gt(maxTakeProfitPrice)
        ? maxTakeProfitPrice
        : position.takeProfit;
    }

    return position.takeProfit.lt(maxTakeProfitPrice) ||
      position.takeProfit.gte(position.entryPrice)
      ? maxTakeProfitPrice
      : position.takeProfit;
  }, [
    collateralPrice,
    currentPairMaxTpRatios,
    input.value.token,
    maxTakeProfitP,
    position.collateral,
    position.entryPrice,
    position.isLong,
    position.size,
    position.takeProfit,
  ]);
};

export const LiquidationPriceChange = ({
  position,
}: {
  position: Position;
}) => {
  const change = useLiquidationPriceChange({ position });

  if (!change?.prev) return <Box />;

  const nextValue = change.next ?? change.nextAtMaxRemovable;

  return (
    <Change
      containerDataCy="RemoveLiquidationPriceChange"
      nextValue={nextValue}
      prevValue={change.prev}
      opts={{
        prefix: '$',
      }}
    />
  );
};
